import { Group, Select } from '@byecode/ui'
import type { Field, IndependentAction, UpdatePageShareAction } from '@lighthouse/core'
import type { NodeTypes, VariableTree2 } from '@lighthouse/shared'
import {
    getFormDatasourceOptions,
    getImageVariableOption,
    getInputInnerTypeMatch,
    getUpstreamActionsOptions,
    getViewVariableOption,
    InnerTypeMapByFieldType,
    ListItem4ByecodeUi,
    NodeFieldPreview,
    nodeTypeOptions,
    PAGE_LINK_OPTION,
    useAtomData,
    VariableSelect
} from '@lighthouse/shared'
import React, { useCallback, useId, useMemo } from 'react'

import { lastPageOfStackAtom, pageAtomFamily } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useActionAdderDepParams } from '@/hooks/useActionAdderDepParams'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'
import { getPexelsList } from '@/services'
import { uploadManagerInAppParams } from '@/utils/auth'

import * as SC from '../styles'

const FILE_FIELD: Field = { id: '', dsId: '', type: 'file', name: '', innerType: InnerTypeMapByFieldType.file }

interface UpdatePageShareActionConfigureProps {
    nodeType?: NodeTypes
    actionTypeSwitcher?: React.ReactNode
    allParentActions?: IndependentAction[]
    value?: UpdatePageShareAction['data']['updatePageShare']
    onChange: (value: UpdatePageShareAction['data']['updatePageShare']) => void
}

export const UpdatePageShareActionConfigure: React.FC<UpdatePageShareActionConfigureProps> = ({
    actionTypeSwitcher,
    nodeType,
    allParentActions,
    value,
    onChange
}) => {
    const actionValue = value ?? {}
    const { image, title, description, link } = actionValue
    const disabledWithVersion = useIsDisabledWithVersion()
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const { viewDsId = '', formDsId = '', viewType, isImage } = useActionAdderDepParams()
    const formDataSource = useDataSource(appId, envId, formDsId)
    const viewDataSource = useDataSource(appId, envId, viewDsId)
    const { pageId } = useAtomData(
        lastPageOfStackAtom,
        useCallback(
            s => ({
                pageId: s?.pageId || ''
            }),
            []
        )
    )
    const pageName = useAtomData(
        pageAtomFamily(pageId),
        useCallback(s => s?.name || '', [])
    )
    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })
    const userOption = useGetUserRecordOptions()
    const upstreamOptions = useMemo(
        () =>
            getUpstreamActionsOptions({
                workflowList,
                allParentActions: allParentActions || [],
                dataSourceList
            }),
        [allParentActions, dataSourceList, workflowList]
    )
    const formOption = useMemo(() => {
        if (!formDataSource) {
            return
        }
        return getFormDatasourceOptions({ dataSource: formDataSource })
    }, [formDataSource])
    const imageOption = useMemo(() => {
        if (isImage) {
            return getImageVariableOption()
        }
    }, [isImage])
    const { customViewOption } = useVariableCustomViewOption()
    const viewOptions = useMemo(() => {
        const options: VariableTree2[] = []
        if (viewDataSource) {
            const viewOption = getViewVariableOption({
                dataSource: viewDataSource,
                viewType
            })
            if (viewOption) {
                options.push(viewOption)
                return options
            }
        }
        if (customViewOption) {
            options.push(customViewOption)
        }
        return options
    }, [customViewOption, viewDataSource, viewType])

    const uploadId = useId()
    const uploadProps = useMemo(
        () => ({
            accept: 'image/*',
            multiple: false,
            uploadOptions: {
                info: { id: uploadId, label: pageName, groupId: pageId },
                options: {
                    ...uploadManagerInAppParams()
                }
            }
        }),
        [pageId, pageName, uploadId]
    )

    return (
        <SC.Container>
            <Group label="动作配置">
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>类型</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        {actionTypeSwitcher || <Select disabled value={nodeType} options={nodeTypeOptions} />}
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            </Group>
            <Group label="分享配置">
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>封面</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <VariableSelect
                            value={image}
                            onChange={nextImage => onChange({ ...actionValue, image: nextImage })}
                            enableFormula
                            innerTypes={getInputInnerTypeMatch(FILE_FIELD.innerType)}
                            disabled={disabledWithVersion}
                            pageOptions={pageOptions}
                            userOption={userOption}
                            formOption={formOption}
                            viewOptions={viewOptions}
                            upstreamOptions={upstreamOptions}
                            imageOption={imageOption}
                            dataSourceList={dataSourceList}
                            field={FILE_FIELD}
                            uploadProps={uploadProps}
                            onFetchPexelsList={getPexelsList}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi alignItems="baseline">
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>标题</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            style={{ width: 180 }}
                            autoHeight
                            pageOptions={pageOptions}
                            upstreamOptions={upstreamOptions}
                            userOption={userOption}
                            formOption={formOption}
                            viewOptions={viewOptions}
                            dataSourceList={dataSourceList}
                            imageOption={imageOption}
                            value={title}
                            onBlur={nextTitle => onChange({ ...actionValue, title: nextTitle })}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi alignItems="baseline">
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>描述</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            style={{ width: 180 }}
                            autoHeight
                            pageOptions={pageOptions}
                            upstreamOptions={upstreamOptions}
                            userOption={userOption}
                            formOption={formOption}
                            viewOptions={viewOptions}
                            dataSourceList={dataSourceList}
                            imageOption={imageOption}
                            value={description}
                            onBlur={nextDescription => onChange({ ...actionValue, description: nextDescription })}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi alignItems="baseline">
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>分享链接</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            style={{ width: 180 }}
                            autoHeight
                            pageOptions={[PAGE_LINK_OPTION, ...(pageOptions || [])]}
                            upstreamOptions={upstreamOptions}
                            userOption={userOption}
                            formOption={formOption}
                            viewOptions={viewOptions}
                            dataSourceList={dataSourceList}
                            imageOption={imageOption}
                            value={link}
                            onBlur={nextLink => onChange({ ...actionValue, link: nextLink })}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            </Group>
        </SC.Container>
    )
}
